import { useId, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { CopyButton } from "./copy.js";
import { QrCode } from "./qr.js";
import type { Translate } from "./types.js";

/** What an authenticator app needs: the otpauth URI (for the QR code) and the manual key. */
export interface EnrollmentSeed {
  otpauthUri: string;
  secret: string;
}

/** The base32 key in groups of four, for typing by hand. */
export function formatKey(secret: string): string {
  return secret
    .replace(/\s+/g, "")
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

/** Step 1: QR code, manual key and the confirming 6-digit code. */
export function TotpEnrollment(props: {
  t: Translate;
  seed: EnrollmentSeed;
  /** Verifies the code. Throw to show `errorText(err)` under the field. */
  confirm: (code: string) => Promise<void>;
  errorText: (err: unknown) => string;
  /** "Step 1 of 2" style progress; omitted when there is only the one screen. */
  step?: { n: number; total: number };
}): ReactNode {
  const { t, seed } = props;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeId = useId();
  const hintId = useId();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    props
      .confirm(code.trim())
      .catch((err: unknown) => {
        setError(props.errorText(err));
        setCode("");
      })
      .finally(() => setBusy(false));
  };

  return (
    <form onSubmit={submit} className="stack" aria-label={t("ui.enroll.title")}>
      {props.step ? <p className="small muted">{t("ui.enroll.step", props.step)}</p> : null}
      <p>{t("ui.enroll.scan")}</p>
      <QrCode value={seed.otpauthUri} label={t("ui.enroll.qrLabel")} />
      <details>
        <summary>{t("ui.enroll.manualTitle")}</summary>
        <div className="stack" style={{ marginTop: "var(--space-2)" }}>
          <span className="small muted">{t("ui.enroll.manualKey")}</span>
          <div className="manual-key">
            <code data-testid="manual-key">{formatKey(seed.secret)}</code>
            <CopyButton t={t} text={seed.secret} />
          </div>
          <a className="btn btn-sm" href={seed.otpauthUri}>
            {t("ui.enroll.openLink")}
          </a>
        </div>
      </details>
      <div className="field">
        <label htmlFor={codeId}>
          <strong className="small">{t("ui.enroll.codeLabel")}</strong>
        </label>
        <input
          id={codeId}
          className="input-code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          maxLength={6}
          aria-describedby={hintId}
          required
        />
        <small id={hintId} className="muted">
          {t("ui.enroll.codeHint")}
        </small>
      </div>
      {error ? (
        <p role="alert" className="note-error">
          {error}
        </p>
      ) : null}
      <button type="submit" className="btn btn-primary" disabled={busy || code.length !== 6}>
        {busy ? t("ui.enroll.confirming") : t("ui.enroll.confirm")}
      </button>
    </form>
  );
}

/** Plain-text file content for the download button. */
export function recoveryFileText(t: Translate, codes: string[]): string {
  return `${t("ui.recovery.fileHeader")}\n${t("ui.recovery.fileNote")}\n\n${codes.join("\n")}\n`;
}

function download(name: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Step 2: the recovery codes, shown once. Continue unlocks after "I saved them". */
export function RecoveryCodes(props: {
  t: Translate;
  codes: string[];
  onDone: () => void;
  step?: { n: number; total: number };
}): ReactNode {
  const { t, codes } = props;
  const [saved, setSaved] = useState(false);
  return (
    <div className="stack">
      {props.step ? <p className="small muted">{t("ui.enroll.step", props.step)}</p> : null}
      <h2>{t("ui.recovery.title")}</h2>
      <p className="muted">{t("ui.recovery.hint")}</p>
      <ul className="recovery-grid" aria-label={t("ui.recovery.title")}>
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="row">
        <CopyButton t={t} text={codes.join("\n")} label={t("ui.recovery.copyAll")} />
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => download("uzverse-recovery-codes.txt", recoveryFileText(t, codes))}
        >
          {t("ui.recovery.download")}
        </button>
      </div>
      <label className="check">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        <span>{t("ui.recovery.saved")}</span>
      </label>
      <button type="button" className="btn btn-primary" disabled={!saved} onClick={props.onDone}>
        {t("ui.recovery.continue")}
      </button>
    </div>
  );
}

/**
 * The whole enrollment: scan and confirm, then the recovery codes once. The seed is only rendered
 * before confirmation; afterwards this component holds just the codes until "Continue".
 */
export function EnrollmentFlow(props: {
  t: Translate;
  seed: EnrollmentSeed;
  /** Confirms the code and returns the recovery codes. Throw on a wrong code. */
  confirm: (code: string) => Promise<string[]>;
  errorText: (err: unknown) => string;
  onFinished: () => void;
}): ReactNode {
  const [codes, setCodes] = useState<string[] | null>(null);
  if (codes) {
    return (
      <RecoveryCodes
        t={props.t}
        codes={codes}
        onDone={props.onFinished}
        step={{ n: 2, total: 2 }}
      />
    );
  }
  return (
    <TotpEnrollment
      t={props.t}
      seed={props.seed}
      errorText={props.errorText}
      step={{ n: 1, total: 2 }}
      confirm={async (code) => {
        setCodes(await props.confirm(code));
      }}
    />
  );
}
